import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, CardActionArea, Grid, Typography, Chip, TextField,
  InputAdornment, Button, MenuItem, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Add, Search, PhotoCamera, Edit, Delete, Storefront,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import PageHeader from '../common/PageHeader';
import AuthImage from '../common/AuthImage';
import { useAuth } from '../../contexts/AuthContext';

const INTEREST_COLOR = { high: 'error', medium: 'warning', low: 'default' };
const STATUS_LABEL = {
  new: 'New', following_up: 'Following up', sample_requested: 'Sample requested',
  sourced: 'Sourced', rejected: 'Not pursuing',
};
const STATUS_COLOR = {
  new: 'info', following_up: 'primary', sample_requested: 'secondary',
  sourced: 'success', rejected: 'default',
};

export default function ExhibitionList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDelete = user?.role === 'admin';

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [interest, setInterest] = useState('all');
  const [show, setShow] = useState('all');
  const [shows, setShows] = useState([]);
  const [confirm, setConfirm] = useState(null);

  const load = async () => {
    const r = await api.get('/exhibition-products');
    setRows(r.data.data || []);
  };
  useEffect(() => {
    load();
    api.get('/exhibition-products/exhibitions').then(r => setShows(r.data.data || [])).catch(() => {});
  }, []);

  const filtered = rows.filter(r => {
    if (status !== 'all' && r.status !== status) return false;
    if (interest !== 'all' && r.interest !== interest) return false;
    if (show !== 'all' && r.exhibition !== show) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = [r.product_name, r.supplier_name, r.category, r.model, r.booth_no, r.notes, r.specifications]
        .filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const doDelete = async () => {
    try { await api.delete(`/exhibition-products/${confirm.id}`); setConfirm(null); load(); }
    catch { setConfirm(null); }
  };

  const priceOf = (r) => r.price != null
    ? `${r.currency || ''} ${parseFloat(r.price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`.trim()
    : null;

  return (
    <Box>
      <PageHeader
        title="Exhibition Finds"
        subtitle="Products spotted at trade shows — photos, prices and follow-ups"
        action={
          <Button variant="contained" startIcon={<PhotoCamera />} onClick={() => navigate('/sourcing/exhibition/new')}>
            Add Product
          </Button>
        }
      />

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <TextField size="small" placeholder="Search product, supplier, booth, notes…"
          value={search} onChange={e => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
        <TextField size="small" select label="Status" value={status} onChange={e => setStatus(e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="all">All status</MenuItem>
          {Object.entries(STATUS_LABEL).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
        </TextField>
        <TextField size="small" select label="Interest" value={interest} onChange={e => setInterest(e.target.value)} sx={{ minWidth: 130 }}>
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="high">High</MenuItem>
          <MenuItem value="medium">Medium</MenuItem>
          <MenuItem value="low">Low</MenuItem>
        </TextField>
        {shows.length > 0 && (
          <TextField size="small" select label="Exhibition" value={show} onChange={e => setShow(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="all">All shows</MenuItem>
            {shows.map(s => <MenuItem key={s.exhibition} value={s.exhibition}>{s.exhibition} ({s.n})</MenuItem>)}
          </TextField>
        )}
      </Box>

      {filtered.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
            <Storefront sx={{ fontSize: 44, opacity: 0.35, mb: 1 }} />
            <Typography variant="body1" gutterBottom>
              {rows.length === 0 ? 'No products recorded yet' : 'Nothing matches those filters'}
            </Typography>
            {rows.length === 0 && (
              <Button sx={{ mt: 1 }} variant="contained" startIcon={<PhotoCamera />}
                onClick={() => navigate('/sourcing/exhibition/new')}>
                Record your first product
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={1.5}>
          {filtered.map(r => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={r.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardActionArea onClick={() => navigate(`/sourcing/exhibition/${r.id}`)}>
                  <Box sx={{ position: 'relative' }}>
                    <AuthImage photoId={r.cover_photo_id} height={165} radius={0} alt={r.product_name || 'product'} />
                    {r.photo_count > 1 && (
                      <Chip size="small" label={`${r.photo_count} photos`}
                        sx={{ position: 'absolute', bottom: 6, right: 6, height: 20, fontSize: 10,
                              bgcolor: 'rgba(0,0,0,.6)', color: '#fff' }} />
                    )}
                    {r.interest && r.interest !== 'medium' && (
                      <Chip size="small" label={r.interest === 'high' ? 'High interest' : 'Low'}
                        color={INTEREST_COLOR[r.interest]}
                        sx={{ position: 'absolute', top: 6, left: 6, height: 20, fontSize: 10 }} />
                    )}
                  </Box>
                  <CardContent sx={{ pb: 1 }}>
                    <Typography variant="body2" fontWeight={700} noWrap>
                      {r.product_name || <em style={{ opacity: 0.6 }}>Untitled product</em>}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                      {r.supplier_name || '—'}{r.booth_no ? ` · Booth ${r.booth_no}` : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, gap: 0.5 }}>
                      <Typography variant="body2" fontWeight={700} color={priceOf(r) ? 'text.primary' : 'text.disabled'} noWrap>
                        {priceOf(r) || 'No price'}
                      </Typography>
                      <Chip size="small" label={STATUS_LABEL[r.status] || r.status || 'New'}
                        color={STATUS_COLOR[r.status] || 'default'} variant="outlined"
                        sx={{ height: 20, fontSize: 10 }} />
                    </Box>
                    {r.moq && <Typography variant="caption" color="text.secondary">MOQ {r.moq}</Typography>}
                  </CardContent>
                </CardActionArea>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pb: 0.5, mt: 'auto' }}>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => navigate(`/sourcing/exhibition/${r.id}/edit`)}>
                      <Edit sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  {canDelete && (
                    <Tooltip title="Delete">
                      <IconButton size="small" sx={{ color: 'error.main' }} onClick={() => setConfirm(r)}>
                        <Delete sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Floating quick-add — the fastest path while walking a show floor */}
      <IconButton onClick={() => navigate('/sourcing/exhibition/new')}
        sx={{ position: 'fixed', right: 20, bottom: 24, width: 56, height: 56, zIndex: 1200,
              bgcolor: 'primary.main', color: '#fff', boxShadow: 4,
              '&:hover': { bgcolor: 'primary.dark' }, display: { xs: 'flex', md: 'none' } }}>
        <Add />
      </IconButton>

      <Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete product</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{confirm?.product_name || 'this product'}</strong> and its photos? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={doDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
